// state/workspace/workspace.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from, forkJoin } from 'rxjs';
import { map, mergeMap, catchError, tap, concatMap, toArray, filter, withLatestFrom, takeUntil } from 'rxjs/operators';
import { WorkspaceService } from '../../services/workspace.service';
import * as WorkspaceActions from './workspace.actions';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectIsLoggedIn } from '../auth/auth.selectors';
import { resetState } from '../global/global.actions';

@Injectable()
export class WorkspaceEffects {
  loadWorkspaces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActions.loadWorkspaces),
      // Check if user is logged in before making the API call
      withLatestFrom(this.store.select(selectIsLoggedIn)),
      // Only proceed if the user is logged in
      filter(([action, isLoggedIn]) => isLoggedIn),
      mergeMap(() =>
        this.workspaceService.getAllWorkspaces().pipe(
          // Cancel this request if resetState is dispatched (user logs out)
          takeUntil(this.actions$.pipe(ofType(resetState))),
          map(response => WorkspaceActions.loadWorkspacesSuccess({
            workspaces: response.workspaces
          })),
          catchError(error => {
            return of(WorkspaceActions.loadWorkspacesFailure({
              error: error.message || 'Failed to load workspaces'
            }));
          })
        )
      )
    )
  );

  loadWorkspaceById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActions.loadWorkspaceById),
      mergeMap(action =>
        this.workspaceService.getWorkspaceById(action.id, action.includeFiles || false).pipe(
          map(response => WorkspaceActions.loadWorkspaceByIdSuccess({
            workspace: response.workspace
          })),
          catchError(error => {
            return of(WorkspaceActions.loadWorkspaceByIdFailure({
              error: error.message || 'Failed to load workspace'
            }));
          })
        )
      )
    )
  );

  createWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActions.createWorkspace),
      mergeMap(action =>
        this.workspaceService.createWorkspace(action.workspace).pipe(
          mergeMap(response => [
            WorkspaceActions.createWorkspaceSuccess({
              workspace: response.workspace
            }),
            // Reload workspaces to ensure the list is updated
            WorkspaceActions.loadWorkspaces()
          ]),
          catchError(error => {
            return of(WorkspaceActions.createWorkspaceFailure({
              error: error.message || 'Failed to create workspace'
            }));
          })
        )
      )
    )
  );

  uploadFilesToWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActions.uploadFilesToWorkspace),
      mergeMap(action => {
        // Since we're now only uploading one file at a time, simplify this
        const file = action.files[0];

        return this.workspaceService.uploadFile(action.workspaceId, file).pipe(
          mergeMap(() => [
            WorkspaceActions.uploadFileSuccess({
              workspaceId: action.workspaceId,
              fileName: file.name
            }),
            WorkspaceActions.uploadFilesComplete({
              workspaceId: action.workspaceId,
              successCount: 1,
              failureCount: 0
            })
          ]),
          catchError(error => {
            return from([
              WorkspaceActions.uploadFileFailure({
                workspaceId: action.workspaceId,
                fileName: file.name,
                error: error.message || 'Failed to upload file'
              }),
              WorkspaceActions.uploadFilesComplete({
                workspaceId: action.workspaceId,
                successCount: 0,
                failureCount: 1
              })
            ]);
          })
        );
      })
    )
  );

  // Handle file deletion
  deleteFileFromWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActions.deleteFileFromWorkspace),
      mergeMap(action =>
        this.workspaceService.deleteFile(action.workspaceId, action.fileId).pipe(
          map(() => WorkspaceActions.deleteFileSuccess({
            workspaceId: action.workspaceId,
            fileId: action.fileId
          })),
          catchError(error => {
            return of(WorkspaceActions.deleteFileFailure({
              error: error.message || 'Failed to delete file'
            }));
          })
        )
      )
    )
  );

  updateWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActions.updateWorkspace),
      mergeMap(action =>
        this.workspaceService.updateWorkspace(action.id, action.changes).pipe(
          map(response => WorkspaceActions.updateWorkspaceSuccess({
            workspace: response.workspace
          })),
          catchError(error => {
            return of(WorkspaceActions.updateWorkspaceFailure({
              error: error.message || 'Failed to update workspace'
            }));
          })
        )
      )
    )
  );

  deleteWorkspace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkspaceActions.deleteWorkspace),
      mergeMap(action =>
        this.workspaceService.deleteWorkspace(action.id).pipe(
          map(() => WorkspaceActions.deleteWorkspaceSuccess({
            id: action.id
          })),
          catchError(error => {
            return of(WorkspaceActions.deleteWorkspaceFailure({
              error: error.message || 'Failed to delete workspace'
            }));
          })
        )
      )
    )
  );

  // Handle navigation after successful deletion
  deleteWorkspaceSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(WorkspaceActions.deleteWorkspaceSuccess),
        tap(() => {
          this.router.navigate(['/workspaces']);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private workspaceService: WorkspaceService,
    private router: Router,
    private store: Store
  ) {}
}
